import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CloseIcon from '@mui/icons-material/Close';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import GetAppOutlinedIcon from '@mui/icons-material/GetAppOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import MarkChatReadOutlinedIcon from '@mui/icons-material/MarkChatReadOutlined';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import AlertCircle from '@mui/icons-material/Warning';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Collapse,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import React, { useEffect, useRef, useState } from 'react';
import { apiFetch } from '../api';
import { API_BASE } from '../apiBase';
import { authErrorMessage } from '../utils/auth-error';

// Required for PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const PUBLISH_STEPS = {
  readPdf: 'Could not read the generated PDF',
  presign: 'Could not get the upload URL',
  upload: 'Could not upload the PDF',
  createRecord: 'Could not save the report',
};

const NO_PDF_MESSAGE =
  'The report PDF is not ready. Go back and generate the preview again.';

/**
 * Runs one network step of the publish flow. Tags a failure with the step that
 * raised it, so the toast and the console name the call that failed.
 */
const runPublishStep = async (stepLabel, action) => {
  try {
    return await action();
  } catch (error) {
    throw new Error(`${stepLabel}: ${error?.message ?? error}`, {
      cause: error,
    });
  }
};

const isUsableBlob = (candidate) =>
  typeof Blob !== 'undefined' &&
  candidate instanceof Blob &&
  candidate.size > 0;

const serverFailure = async (response) => {
  const body = await response.json().catch(() => null);
  const reason = body?.reason
    ? authErrorMessage(body)
    : (body?.error ?? body?.message);
  const status = `the server returned HTTP ${response.status}`;
  return typeof reason === 'string' && reason ? `${status}: ${reason}` : status;
};

const CompPreview = ({
  pdfUrl,
  pdfBlob,
  onBack,
  reportData,
  geoData,
  onContextUpdate,
}) => {
  const isMobile = /Mobi|Android/i.test(navigator.userAgent);
  const hasPdf = isUsableBlob(pdfBlob) || Boolean(pdfUrl);
  const [imageSrc, setImageSrc] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const publishingRef = useRef(false);
  // Latched once a publish succeeds. The success toast sits for four seconds
  // before the redirect, and the page stays live, so without this the analyst
  // can publish the same report a second time.
  const [publishComplete, setPublishComplete] = useState(false);
  const publishCompleteRef = useRef(false);
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [savingDraft, setSavingDraft] = useState(false);
  const [showPublishConfirmation, setShowPublishConfirmation] = useState(false);
  const [showPublishOptions, setShowPublishOptions] = useState(false);
  const [reportStatus, setReportStatus] = useState(reportData?.status || null);
  const [reportId, setReportId] = useState(reportData?.report_id || null);
  console.log('Report Id: ', reportId);
  console.log('Status: ', reportStatus);
  const [publishActionType, setPublishActionType] = useState(null);
  const [mapPDF, setMapPDF] = useState(null);
  const [recordCounts, setRecordCounts] = useState({
    total: 0,
    offers: 0,
    feedback: 0,
    rows: 0,
  });
  const {
    reportTitle = 'Listings Report',
    rowOrder = [],
    columnSettings = [],
    drawnFeatures = { polygons: [], markers: [] },
    selectedRows = [],
    report_data = [],
    report_id = '',
  } = reportData;
  console.log('ListingPreview geoData:', {
    hasGeoData: !!geoData,
    keys: geoData ? Object.keys(geoData) : null,
    sampleFeature: geoData ? geoData[Object.keys(geoData)[0]] : null,
  });

  useEffect(() => {
    if (reportData?.mapPDF) {
      setMapPDF(reportData.mapPDF);
    }
  }, [reportData]);

  // Add download map handler
  const handleDownloadMap = () => {
    if (!mapPDF) return;

    const mapUrl = URL.createObjectURL(mapPDF);

    const isMobile = /Mobi|Android|iPhone/i.test(navigator.userAgent);

    if (isMobile) {
      // Open in new tab to preserve state
      window.open(mapUrl, '_blank');
    } else {
      // Force download with filename
      const a = document.createElement('a');
      a.href = mapUrl;
      a.download = `${reportData.reportTitle.replace(/\s+/g, '_')}_Map.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }

    // Clean up
    setTimeout(() => URL.revokeObjectURL(mapUrl), 1000);
  };

  const createPayload = (status) => {
    const buffer = 0.01;
    let centerLat = 0;
    let centerLng = 0;
    let minLat = null;
    let maxLat = null;
    let minLng = null;
    let maxLng = null;
    let polygonIds = [];

    // Process geoData if available
    if (geoData && Object.keys(geoData).length > 0) {
      const polygonId = Object.keys(geoData)[0]; // Get the first/only polygon ID
      const polygon = geoData[polygonId];
      polygonIds = [polygonId];

      // Always use properties.latitude/longitude if available
      if (
        polygon?.properties?.latitude != null &&
        polygon?.properties?.longitude != null
      ) {
        centerLat = parseFloat(polygon.properties.latitude);
        centerLng = parseFloat(polygon.properties.longitude);

        // Calculate bounds from center + buffer
        minLat = centerLat - buffer;
        maxLat = centerLat + buffer;
        minLng = centerLng - buffer;
        maxLng = centerLng + buffer;
      } else {
        // Fallback to geometry calculation (shouldn't happen based on your data)
        console.warn('No properties.latitude/longitude, using geometry');
        const coords = polygon.geometry.coordinates[0].map((coord) => ({
          lat: coord[1],
          lng: coord[0],
        }));

        minLat = Math.min(...coords.map((c) => c.lat));
        maxLat = Math.max(...coords.map((c) => c.lat));
        minLng = Math.min(...coords.map((c) => c.lng));
        maxLng = Math.max(...coords.map((c) => c.lng));

        centerLat = (minLat + maxLat) / 2;
        centerLng = (minLng + maxLng) / 2;

        // Apply buffer
        minLat -= buffer;
        maxLat += buffer;
        minLng -= buffer;
        maxLng += buffer;
      }
    }

    const payload = {
      ...(reportId ? { report_id: reportId } : {}),
      title: reportTitle,
      row_order: {
        offers:
          reportData.offerRowOrder ||
          reportData.offers?.map((row) => row.id) ||
          [],
        feedback:
          reportData.feedbackRowOrder ||
          reportData.feedback?.map((row) => row.id) ||
          [],
      },
      column_settings: columnSettings,
      drawn_polygons: drawnFeatures.polygons || [],
      marker_radii: drawnFeatures.markers || [],
      selected_polygons: polygonIds.map((id) => {
        const latRaw = geoData?.[id]?.properties?.latitude;
        const lngRaw = geoData?.[id]?.properties?.longitude;

        const lat = typeof latRaw === 'number' ? latRaw : parseFloat(latRaw);
        const lng = typeof lngRaw === 'number' ? lngRaw : parseFloat(lngRaw);

        return {
          id,
          lat: isNaN(lat) ? null : lat,
          lng: isNaN(lng) ? null : lng,
        };
      }),
      report_center_lat: centerLat,
      report_center_lng: centerLng,
      bounds_min_lat: minLat,
      bounds_max_lat: maxLat,
      bounds_min_lng: minLng,
      bounds_max_lng: maxLng,
      status,
      report_data: {
        offers: reportData.offers || [],
        feedback: reportData.feedback || [],
      },
      geo_data: geoData,
      action_type: 'insert_new', // Make sure this is set
    };

    console.log('Payload verification:', {
      center: `${centerLat}, ${centerLng}`,
      bounds: `Lat: ${minLat}-${maxLat}, Lng: ${minLng}-${maxLng}`,
      selectedPolygons: payload.selected_polygons,
      hasGeoData: !!geoData,
    });
    return payload;
  };

  const handlePublishClick = () => {
    // Debug: Log current report data structure
    console.log('[PUBLISH CLICK] Current reportData:', {
      id: reportId,
      status: reportStatus,
      title: reportData?.reportTitle,
      rows: reportData?.rows?.length || 0,
      offers: reportData?.reportOfferCount?.length || 0,
      feedback: reportData?.reportFeedbackCount?.length || 0,
      geoData: reportData?.geoData ? 'Exists' : 'Missing',
    });

    // Calculate total records
    const totalRecords =
      (reportData?.rows?.length || 0) +
      (reportData?.reportOfferCount?.length || 0) +
      (reportData?.reportFeedbackCount?.length || 0);

    console.log('[PUBLISH CLICK] Calculated counts:', {
      total: totalRecords,
      offers: reportData?.reportOfferCount?.length || 0,
      feedback: reportData?.reportFeedbackCount?.length || 0,
      rows: reportData?.rows?.length || 0,
    });

    // Determine action type
    let actionTypeGuess;
    if (!reportId) {
      actionTypeGuess = 'insert_new';
    } else if (reportStatus === 'draft') {
      actionTypeGuess = 'overwrite';
    } else if (reportStatus === 'published') {
      actionTypeGuess = 'overwrite'; // default guess; refined later by user
    } else {
      actionTypeGuess = 'insert_new';
    }

    // Store counts in state to ensure they're available when modal renders
    setRecordCounts({
      total: totalRecords,
      offers: reportData?.reportOfferCount?.length || 0,
      feedback: reportData?.reportFeedbackCount?.length || 0,
    });

    setPublishActionType(actionTypeGuess);
    setShowPublishConfirmation(true);
  };

  const getSaveActionType = () => {
    if (!reportId) return 'insert_new';
    if (reportStatus === 'published') return 'insert_new'; // Creates new draft
    return 'overwrite'; // For existing drafts
  };

  const getPublishActionType = (userWantsNewVersion = false) => {
    if (!reportId) return 'insert_new';
    if (reportStatus === 'draft') return 'overwrite'; // Convert draft to published
    return userWantsNewVersion ? 'insert_new' : 'overwrite'; // Published report options
  };

  /**
   * Returns the generated PDF. Prefers the blob held in memory, because the
   * preview object URL can already be revoked by the time the user publishes.
   */
  const resolvePdfBlob = async () => {
    if (isUsableBlob(pdfBlob)) return pdfBlob;
    if (!pdfUrl) throw new Error('the preview produced no PDF');

    const pdfResponse = await fetch(pdfUrl);
    if (!pdfResponse.ok) {
      throw new Error(`the preview returned HTTP ${pdfResponse.status}`);
    }
    const fetched = await pdfResponse.blob();
    if (fetched.size === 0) throw new Error('the generated PDF is empty');
    return fetched;
  };

  const requestUploadUrl = (fileName, contentType) =>
    runPublishStep(PUBLISH_STEPS.presign, async () => {
      const presignRes = await apiFetch(`${API_BASE}/presign/report-upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName, contentType }),
      });
      if (!presignRes.ok) {
        throw new Error(await serverFailure(presignRes));
      }
      return presignRes.json();
    });

  const putToStorage = (uploadUrl, blob, contentType) =>
    runPublishStep(PUBLISH_STEPS.upload, async () => {
      const uploadRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: blob,
      });
      if (!uploadRes.ok) {
        throw new Error(`storage returned HTTP ${uploadRes.status}`);
      }
    });

  /**
   * Uploads the PDF and returns the stored object key. The upload URL expires
   * five minutes after it is signed, so a first failure earns one retry against a
   * freshly signed URL before the publish gives up.
   */
  const uploadPdf = async (blob, fileName, contentType) => {
    const firstAttempt = await requestUploadUrl(fileName, contentType);
    try {
      await putToStorage(firstAttempt.uploadUrl, blob, contentType);
      return firstAttempt.key;
    } catch (error) {
      console.warn('Upload failed, retrying with a fresh upload URL:', error);
      const retryAttempt = await requestUploadUrl(fileName, contentType);
      await putToStorage(retryAttempt.uploadUrl, blob, contentType);
      return retryAttempt.key;
    }
  };

  const handlePublishConfirm = async (userWantsNewVersion = false) => {
    // A second click lands before the dialog unmounts, so the ref, not the
    // state, is what stops a double publish writing two report rows.
    if (publishingRef.current || publishCompleteRef.current) return;
    publishingRef.current = true;

    try {
      setPublishing(true);
      setShowPublishConfirmation(false);

      const fileName = `${reportTitle?.replace(/\s+/g, '_') || 'report'}.pdf`;
      const contentType = 'application/pdf';

      // 1. Take the generated PDF from memory, or read the preview URL.
      const publishBlob = await runPublishStep(
        PUBLISH_STEPS.readPdf,
        resolvePdfBlob,
      );

      // 2. Sign an upload URL and 3. put the PDF in storage. A rejected upload
      // stops the publish, so no report row names an object that never landed.
      const key = await uploadPdf(publishBlob, fileName, contentType);

      // // 4. Prepare geographical data
      // const selectedPolygon = geoData?.selectedPolygon;
      // const reportCenterLat = selectedPolygon?.center?.lat || 0;
      // const reportCenterLng = selectedPolygon?.center?.lng || 0;
      //
      // // Calculate bounds if available
      // let bounds = { minLat: null, maxLat: null, minLng: null, maxLng: null };
      // if (selectedPolygon?.geometry?.coordinates) {
      //   const coords = selectedPolygon.geometry.coordinates[0];
      //   const lats = coords.map(coord => coord[1]);
      //   const lngs = coords.map(coord => coord[0]);
      //   bounds = {
      //     minLat: Math.min(...lats),
      //     maxLat: Math.max(...lats),
      //     minLng: Math.min(...lngs),
      //     maxLng: Math.max(...lngs)
      //   };
      // }

      // 5. Prepare report data structure
      const reportDataPayload = {};

      // Only include offers if they exist
      if (reportData?.offers?.length) {
        reportDataPayload.offers = reportData.offers;
      }

      // Only include feedback if it exists
      if (reportData?.feedback?.length) {
        reportDataPayload.feedback = reportData.feedback;
      }

      // 6. Prepare column settings structure
      const columnSettingsPayload = {
        offers: reportData?.columnSettings?.offers || {},
        feedback: reportData?.columnSettings?.feedback || {},
      };

      // 7. Create payload with all required fields
      const status = 'published';
      const payload = createPayload(status);
      // payload.pdf_base64 = base64String;
      payload.pdf_s3_key = key;
      // // Geographical data
      // payload.report_center_lat = reportCenterLat;
      // payload.report_center_lng = reportCenterLng;
      // payload.bounds_min_lat = bounds.minLat;
      // payload.bounds_max_lat = bounds.maxLat;
      // payload.bounds_min_lng = bounds.minLng;
      // payload.bounds_max_lng = bounds.maxLng;
      // payload.selected_polygons = selectedPolygon ? [selectedPolygon] : [];
      // payload.marker_radii = []; // Add your marker radii if available
      // payload.drawn_polygons = []; // Add drawn polygons if available

      // Report data and structure
      payload.report_data = reportDataPayload;
      payload.column_settings = columnSettingsPayload;

      // If you have row_order for offers/feedback separately
      payload.row_order = {
        offers: reportData?.offers?.map((offer) => offer.id) || [],
        feedback: reportData?.feedback?.map((fb) => fb.id) || [],
      };

      payload.filters = {}; // Add any filters applied

      const actionType = getPublishActionType(userWantsNewVersion);
      payload.action_type = actionType;

      if (actionType === 'overwrite') {
        payload.report_id = reportId;
      }

      // 8. Check final size
      const payloadSizeMB = JSON.stringify(payload).length / (1024 * 1024);
      console.log(`Payload size: ${payloadSizeMB.toFixed(2)}MB`);
      console.log('Full Publish Payload: ', payload);

      if (payloadSizeMB > 6) {
        throw new Error(
          `Final payload too large (${payloadSizeMB.toFixed(2)}MB). Try reducing image quality in settings.`,
        );
      }

      console.group('🚀 Sending Payload to Lambda');
      console.log('API Endpoint:', `${API_BASE}/listings/reports/create`);
      console.log(
        'Payload Size:',
        `${(JSON.stringify(payload).length / 1024).toFixed(2)} KB`,
      );
      console.log('Final Payload Preview:', {
        selected_polygons: payload.selected_polygons,
        geo_data_sample: payload.geo_data
          ? Object.keys(payload.geo_data).slice(0, 3)
          : null,
        report_center: `${payload.report_center_lat}, ${payload.report_center_lng}`,
      });
      console.groupEnd();

      // 9. Save the report row that points at the stored PDF
      const responseData = await runPublishStep(
        PUBLISH_STEPS.createRecord,
        async () => {
          const response = await apiFetch(
            `${API_BASE}/listings/reports/create`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            },
          );
          if (!response.ok) {
            throw new Error(await serverFailure(response));
          }
          return response.json();
        },
      );

      // Update local state if we get a new report_id
      if (responseData.report_id && actionType === 'insert_new') {
        setReportId(responseData.report_id);
        setReportStatus('published');
      }

      handleSuccessfulPublish(publishBlob, responseData.report_id);
    } catch (error) {
      console.error('Publish error:', error);
      setToast({
        open: true,
        message: error.message,
        severity: 'error',
        autoHideDuration: 10000,
      });
    } finally {
      publishingRef.current = false;
      setPublishing(false);
    }
  };

  const handlePublishCancel = () => {
    setShowPublishConfirmation(false);
  };

  // Preserve existing successful publish handling
  const handleSuccessfulPublish = (blob, newReportId) => {
    publishCompleteRef.current = true;
    setPublishComplete(true);

    const sanitizedName = reportTitle
      .replace(/[^a-zA-Z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '_')
      .substring(0, 100);

    const blobUrl = URL.createObjectURL(blob);

    if (isMobile) {
      // 🔄 Open in new tab to avoid "back" issues
      window.open(blobUrl, '_blank');

      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 10000);
    } else {
      // 💾 Download
      const desktopDownloadLink = document.createElement('a');
      desktopDownloadLink.href = blobUrl;
      desktopDownloadLink.download = `${sanitizedName}.pdf`;
      document.body.appendChild(desktopDownloadLink);
      desktopDownloadLink.click();
      document.body.removeChild(desktopDownloadLink);

      // 🖼 Optional: Desktop preview (inline iframe)
      try {
        const previewWindow = window.open('', '_blank');
        if (previewWindow && !previewWindow.closed) {
          previewWindow.document.write(`
            <iframe
              src="${blobUrl}"
              style="width:100%;height:100%;border:none"
              title="PDF Preview"
            ></iframe>
          `);
        }
      } catch (e) {
        console.log('Preview window could not be opened', e);
      }

      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    }

    // ✅ Toast
    setToast({
      open: true,
      message:
        reportStatus === 'published'
          ? 'Report updated successfully! Returning to map...'
          : 'Report published successfully! Returning to map...',
      severity: 'success',
    });

    // ✅ Redirect back (after 4 seconds)
    setTimeout(() => {
      window.location.href = '/';
    }, 4000);
  };

  const handleSaveDraft = async () => {
    console.log('Save Draft button clicked');
    setSavingDraft(true); // Start loading

    try {
      const actionType = getSaveActionType();
      const status = 'draft';
      const payload = createPayload(status);
      payload.action_type = actionType;

      if (actionType === 'overwrite') {
        payload.report_id = reportId;
      }

      if (geoData) {
        payload.geo_data = geoData;
      }

      // console.log("Draft payload:", JSON.stringify(payload, null, 2));

      const response = await apiFetch(`${API_BASE}/listings/reports/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(await serverFailure(response));
      }

      const responseData = await response.json();

      // Update state and show success message
      if (responseData.report_id && actionType === 'insert_new') {
        setReportId(responseData.report_id);
        setReportStatus('draft');
      }

      setToast({
        open: true,
        message: reportId
          ? 'Draft updated successfully!'
          : 'Draft saved successfully!',
        severity: 'success',
        autoHideDuration: 3000, // Show for 3 seconds
      });
    } catch (error) {
      console.error('Save draft error:', error);
      setToast({
        open: true,
        message: `Failed to save draft: ${error.message}`,
        severity: 'error',
        autoHideDuration: 5000, // Show errors longer
      });
    } finally {
      setSavingDraft(false); // End loading
    }
  };

  const handleBack = () => {
    const returnData = {
      reportGeoData: geoData,
      currentRows: reportData?.currentRows || [],
      currentCartItems: reportData?.currentCartItems || [],
      currentRowOrder: reportData?.currentRowOrder || [],
      columnSettings: reportData?.columnSettings || {},
      dataGridState: reportData?.dataGridState || null,
      excludeOffers: reportData?.excludeOffers || false,
      excludeFeedback: reportData?.excludeFeedback || false,

      // Ensure we're passing the actual data arrays
      offers: Array.isArray(reportData?.offers) ? [...reportData.offers] : [],
      feedback: Array.isArray(reportData?.feedback)
        ? [...reportData.feedback]
        : [],
      offerRowOrder: Array.isArray(reportData?.offerRowOrder)
        ? [...reportData.offerRowOrder]
        : [],
      feedbackRowOrder: Array.isArray(reportData?.feedbackRowOrder)
        ? [...reportData.feedbackRowOrder]
        : [],
      offerSortModel: reportData?.offerSortModel || [],
      feedbackSortModel: reportData?.feedbackSortModel || [],
      statusFilter: reportData?.statusFilter || [],
      dateFilter: reportData?.dateFilter || null,
      activeTab: reportData?.activeTab || 0,
    };

    console.log('DEBUG - Returning to builder with:', {
      offers: returnData.offers.length,
      feedback: returnData.feedback.length,
    });

    onBack(returnData);
  };

  // Preserve existing PDF to image conversion
  useEffect(() => {
    const renderPDFtoImage = async () => {
      if (!isMobile || !pdfUrl) return;

      try {
        const loadingTask = pdfjsLib.getDocument(pdfUrl);
        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 2 });

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        await page.render({ canvasContext: context, viewport }).promise;
        const imageDataUrl = canvas.toDataURL('image/png');
        setImageSrc(imageDataUrl);
      } catch (error) {
        console.error('❌ Failed to render PDF to image:', error);
      }
    };

    renderPDFtoImage();
  }, [pdfUrl, isMobile]);

  useEffect(() => {
    console.log('[COMP PREVIEW MOUNT] Initial reportData:', {
      rows: reportData?.rows?.length,
      offers: reportData?.offers,
      feedback: reportData?.feedback,
    });
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Main content - preserved exactly as before */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          p: 2,
          backgroundColor: '#f5f5f5',
          position: 'relative',
        }}
      >
        {/* Existing PDF preview rendering */}
        {pdfUrl ? (
          <>
            {isMobile ? (
              imageSrc ? (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'white',
                    borderRadius: 1,
                    overflow: 'hidden',
                    boxShadow: 1,
                  }}
                >
                  <img
                    src={imageSrc}
                    alt='PDF Preview'
                    style={{ width: '100%', height: 'auto' }}
                  />
                </Box>
              ) : (
                <Box sx={{ p: 2 }}>Generating preview…</Box>
              )
            ) : (
              <Box
                sx={{
                  flex: 1,
                  backgroundColor: 'white',
                  borderRadius: 1,
                  overflow: 'hidden',
                  boxShadow: 1,
                  '& iframe': {
                    width: '100%',
                    height: '100%',
                    minHeight: 'calc(100vh - 150px)',
                    border: 'none',
                  },
                }}
              >
                <iframe
                  src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                  title='PDF Preview'
                />
              </Box>
            )}
          </>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'white',
              borderRadius: 1,
              boxShadow: 1,
              p: 3,
            }}
          >
            <Alert severity='warning' sx={{ maxWidth: 520 }}>
              {NO_PDF_MESSAGE}
            </Alert>
          </Box>
        )}

        {/* Replace the Comment Sticky Button with this rounded square version */}
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 1000,
            display: 'flex',
            gap: 1.5, // Controls spacing between icons
          }}
        >
          {mapPDF && (
            <Tooltip title='Download Map PDF'>
              <IconButton
                onClick={handleDownloadMap}
                sx={{
                  backgroundColor: 'transparent',
                  '&:hover': { backgroundColor: 'action.hover' },
                  p: 0,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#2196F3',
                    borderRadius: 2,
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 1,
                    transition: 'all 0.2s ease',
                    '&:hover': { transform: 'scale(1.05)' },
                  }}
                >
                  <MapOutlinedIcon
                    sx={{ color: 'white', fontSize: '1.25rem' }}
                  />
                </Box>
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Footer with all buttons - Add this */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          p: 2,
          borderTop: '1px solid #e0e0e0',
          backgroundColor: 'white',
        }}
      >
        {/* Back Button */}
        <Box
          sx={{
            backgroundColor: '#f5f5f5',
            borderRadius: 2,
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid #e0e0e0',
          }}
        >
          <IconButton onClick={handleBack}>
            <ArrowBackOutlinedIcon sx={{ color: 'action.active' }} />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Publish Button */}
          <Button
            variant='contained'
            color='primary'
            startIcon={
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1,
                }}
              >
                <GetAppOutlinedIcon sx={{ color: 'white', fontSize: '1rem' }} />
              </Box>
            }
            onClick={handlePublishClick}
            disabled={publishing || publishComplete || !hasPdf}
            sx={{
              px: 2,
              textTransform: 'none',
              fontWeight: 'bold',
            }}
          >
            {publishing ? (
              <>
                Publishing...
                <CircularProgress size={24} sx={{ ml: 1 }} />
              </>
            ) : (
              'PUBLISH REPORT'
            )}
          </Button>
        </Box>
      </Box>
      {/* Enhanced Publish Confirmation Modal */}
      {showPublishConfirmation && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Card
            sx={{
              width: '100%',
              maxWidth: 500,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <AlertCircle sx={{ color: 'white' }} />
              <Typography variant='h6'>Publish Report Options</Typography>
            </Box>

            {/* Content */}
            <Box sx={{ p: 3 }}>
              <Typography variant='body1' sx={{ mb: 2 }}>
                {publishActionType === 'insert_new'
                  ? 'You’re about to publish a new report. Please confirm before proceeding.'
                  : 'You’re editing a previously published report. What would you like to do?'}
              </Typography>

              <Box
                sx={{
                  backgroundColor: 'grey.100',
                  p: 2,
                  borderRadius: 1,
                  mb: 2,
                }}
              >
                <Typography variant='subtitle2' color='text.secondary'>
                  Report Title:
                </Typography>
                <Typography
                  variant='body1'
                  sx={{ fontWeight: 'medium', mb: 2 }}
                >
                  {reportData?.reportTitle || 'Untitled Report'}
                </Typography>

                <Typography variant='subtitle2' color='text.secondary'>
                  Records Included:
                </Typography>
                <Typography variant='body1' sx={{ fontWeight: 'medium' }}>
                  {recordCounts.total} Records
                  {recordCounts.offers > 0 && ` (${recordCounts.offers} offers`}
                  {recordCounts.feedback > 0 &&
                    `, ${recordCounts.feedback} feedback)`}
                </Typography>
              </Box>

              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ fontStyle: 'italic' }}
              >
                You can choose to overwrite the existing report or create a
                brand new version.
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
                p: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              {!showPublishOptions ? (
                <>
                  <Button
                    variant='outlined'
                    onClick={handlePublishCancel}
                    sx={{ textTransform: 'none' }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant='outlined'
                    onClick={handleSaveDraft}
                    disabled={savingDraft}
                    startIcon={
                      savingDraft ? (
                        <CircularProgress size={20} />
                      ) : (
                        <SaveOutlinedIcon />
                      )
                    }
                    sx={{
                      textTransform: 'none',
                      minWidth: 120, // Ensure consistent width
                    }}
                  >
                    {savingDraft ? 'Saving...' : 'Save Draft'}
                  </Button>
                  <Button
                    variant='contained'
                    color='primary'
                    disabled={publishing || publishComplete}
                    onClick={() => {
                      if (reportStatus === 'published' && reportId) {
                        setShowPublishOptions(true); // step into secondary choice
                      } else {
                        handlePublishConfirm(false); // skip second step
                      }
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Publish
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant='outlined'
                    onClick={handlePublishCancel}
                    sx={{ textTransform: 'none' }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant='outlined'
                    disabled={publishing || publishComplete}
                    onClick={() => handlePublishConfirm(true)}
                    sx={{ textTransform: 'none' }}
                  >
                    Create New Report
                  </Button>
                  <Button
                    variant='contained'
                    color='primary'
                    disabled={publishing || publishComplete}
                    onClick={() => handlePublishConfirm(false)}
                    sx={{ textTransform: 'none' }}
                  >
                    Overwrite Existing
                  </Button>
                </>
              )}
            </Box>
          </Card>
        </Box>
      )}
      <Snackbar
        open={toast.open}
        autoHideDuration={toast.autoHideDuration || 3000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompPreview;
